/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Cpu,
  Shield,
  Server,
  Cloud,
  Network,
  Zap,
  ArrowRight,
  Check,
  Menu,
  X,
  Activity,
  Globe,
  Lock,
  Terminal,
} from "lucide-react";
import Hyperspeed from "./Hyperspeed";
import NetworkBackground from "./NetworkBackground";
import Logo from "./Logo";
import TiltCard from "./TiltCard";

const IMG = {
  dataCenter: "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg",
  wires: "https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg",
};

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Overline = ({ children, testid }) => (
  <span data-testid={testid} className="font-mono text-xs tracking-[0.3em] uppercase text-[#76b900]">
    {children}
  </span>
);

const SectionHeader = ({ overline, title, text, centered = false }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-100px" }}
    variants={fadeUp}
    className={`${centered ? "mx-auto text-center" : ""} max-w-3xl`}
  >
    <Overline>{overline}</Overline>
    <h2 className="font-display font-bold tracking-tighter text-4xl md:text-5xl mt-4">{title}</h2>
    {text && <p className="mt-5 text-white/60 text-lg font-light">{text}</p>}
  </motion.div>
);

const Card = ({ children, className = "", testid, image, spotlight = false }) => (
  <TiltCard
    variants={fadeUp}
    max={7}
    lift={6}
    scale={1.015}
    parallaxChildren
    spotlight={spotlight}
    testid={testid}
    outerClassName={className}
    className="group liquid-glass relative h-full overflow-hidden rounded-2xl p-8 transition-colors duration-300"
  >
    {image && (
      <div className="absolute inset-0 opacity-15 group-hover:opacity-25 transition-opacity duration-500">
        <img src={image} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/75 to-transparent" />
      </div>
    )}
    <div className="relative z-[2]">{children}</div>
  </TiltCard>
);

export const Navbar = ({ homePath = "" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Pourquoi CARTHY", href: `${homePath}#why` },
    { label: "Services", href: `${homePath}#services` },
    { label: "Produits", href: "/produits", current: Boolean(homePath) },
    { label: "CARTHY AI", href: `${homePath}#carthy-ai` },
    { label: "Conformité", href: `${homePath}#compliance` },
    { label: "À propos", href: `${homePath}#about` },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-black/70 backdrop-blur-2xl border-white/10" : "border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 h-20 flex items-center justify-between">
        <a href={`${homePath}#top`} data-testid="logo" className="flex items-center gap-3 group shrink-0" aria-label={homePath ? "Retour à l’accueil" : "Retour en haut"}>
          <Logo size={36} testid="navbar-logo" />
        </a>

        <nav aria-label="Navigation principale" className="hidden xl:flex items-center gap-5">
          {links.map((link) => (
            <a key={link.label} href={link.href} aria-current={link.current ? "page" : undefined} className={`text-sm transition-colors ${link.current ? "text-[#76b900]" : "text-white/70 hover:text-white"}`}>
              {link.label}
            </a>
          ))}
        </nav>

        <a
          href={`${homePath}#contact`}
          className="hidden xl:inline-flex items-center gap-2 rounded-full bg-[#76b900] px-5 py-2.5 text-sm font-semibold text-black hover:bg-[#8ce500] transition-colors shadow-[0_0_24px_rgba(118,185,0,0.35)]"
        >
          Planifier une rencontre <ArrowRight size={15} />
        </a>

        <button
          type="button"
          aria-label="Ouvrir le menu"
          aria-expanded={open}
          aria-controls="mobile-navigation"
          className="xl:hidden text-white"
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div id="mobile-navigation" className="xl:hidden bg-black/95 border-t border-white/10 px-6 py-6 flex flex-col gap-4">
          {links.map((link) => (
            <a key={link.label} href={link.href} aria-current={link.current ? "page" : undefined} onClick={() => setOpen(false)} className={`py-1 ${link.current ? "text-[#76b900]" : "text-white/80"}`}>
              {link.label}
            </a>
          ))}
          <a href={`${homePath}#contact`} onClick={() => setOpen(false)} className="mt-2 rounded-full bg-[#76b900] px-5 py-3 text-center font-semibold text-black">
            Planifier une rencontre
          </a>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden bg-black">
    <div className="absolute inset-0 z-0">
      <Hyperspeed />
    </div>
    <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/45 via-black/10 to-black pointer-events-none" />

    <div className="relative z-20 min-h-screen mx-auto max-w-7xl px-6 py-28 flex flex-col justify-center pointer-events-none">
      <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}>
        <Overline testid="hero-overline">Données sous contrôle</Overline>
      </motion.div>

      <motion.h1 initial="hidden" animate="show" variants={fadeUp} custom={1} className="mt-6 max-w-5xl">
        <img
          src="/brand/carthy-wordmark.png"
          alt="CARTHY"
          className="w-full max-w-[760px] select-none pointer-events-none drop-shadow-[0_0_48px_rgba(118,185,0,0.35)]"
          draggable={false}
        />
        <span className="block mt-8 font-display font-semibold tracking-tighter text-4xl md:text-6xl lg:text-7xl leading-[0.98]">
          Infrastructure, conformité et <span className="text-[#76b900]">IA privée</span> pour les entreprises
        </span>
      </motion.h1>

      <motion.p
        initial="hidden"
        animate="show"
        variants={fadeUp}
        custom={2}
        className="mt-7 max-w-3xl text-base md:text-lg text-white/70 font-light"
      >
        CARTHY accompagne les organisations qui veulent sécuriser leur infrastructure informatique,
        répondre aux exigences SOC 2 et ISO 27001 et déployer une intelligence artificielle locale
        sans exposer leurs données sensibles.
      </motion.p>

      <motion.div
        initial="hidden"
        animate="show"
        variants={fadeUp}
        custom={3}
        className="mt-10 flex flex-wrap items-center gap-4 pointer-events-auto"
      >
        <a
          href="#contact"
          className="group inline-flex items-center gap-2 rounded-full bg-[#76b900] px-7 py-3.5 text-sm font-semibold text-black hover:bg-[#8ce500] transition-colors shadow-[0_0_32px_rgba(118,185,0,0.4)]"
        >
          Planifier une rencontre <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </a>
        <a
          href="#services"
          className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:border-[#76b900] hover:text-[#76b900] transition-colors"
        >
          <Terminal size={16} /> Découvrir nos services
        </a>
      </motion.div>

      <motion.p initial="hidden" animate="show" variants={fadeUp} custom={4} className="mt-8 max-w-2xl text-sm text-white/45">
        Pensé pour les entreprises qui évoluent dans des environnements exigeants : finance, santé,
        technologie et opérations critiques.
      </motion.p>
    </div>

    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 font-mono text-[10px] tracking-[0.3em] text-white/40 uppercase">
      Maintenez le clic — accélérez
    </div>
  </section>
);

const TrustStrip = () => {
  const sectors = ["Finance", "Santé", "Technologies", "Opérations critiques"];
  return (
    <section className="border-y border-white/10 bg-black/55 backdrop-blur-sm py-7 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
        <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#76b900]">Secteurs prioritaires</span>
        {sectors.map((sector) => (
          <span key={sector} className="font-display font-semibold text-white/45">{sector}</span>
        ))}
      </div>
    </section>
  );
};

const WhyCarthy = () => {
  const values = [
    {
      icon: Shield,
      title: "Protection renforcée",
      text: "Sécurisez vos systèmes et votre infrastructure sans perdre en agilité opérationnelle.",
    },
    {
      icon: Activity,
      title: "Conformité structurée",
      text: "Avancez vers SOC 2 Type 1, SOC 2 Type 2 et ISO 27001 avec une démarche claire et progressive.",
    },
    {
      icon: Lock,
      title: "Données sous contrôle",
      text: "Déployez une IA privée locale afin de conserver vos données stratégiques dans votre périmètre de sécurité.",
    },
  ];

  return (
    <section id="why" className="relative bg-black py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          overline="Pourquoi CARTHY"
          title="Une approche conçue pour les environnements où le risque n'est pas une option"
          text="Les cybermenaces, les exigences réglementaires et la protection des données imposent une infrastructure plus robuste, mieux contrôlée et conforme aux standards reconnus du marché."
        />

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          transition={{ staggerChildren: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16"
        >
          {values.map((value) => {
            const Icon = value.icon;
            return (
              <Card key={value.title} testid={`value-${value.title}`}>
                <Icon className="text-[#76b900]" size={28} />
                <h3 className="font-display font-semibold text-2xl mt-6">{value.title}</h3>
                <p className="mt-3 text-white/60 font-light">{value.text}</p>
              </Card>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

const Services = () => {
  const services = [
    {
      icon: Server,
      title: "Gestion d'infrastructure informatique",
      text: "Nous concevons, organisons et soutenons des environnements technologiques robustes adaptés aux réalités des PME et des grandes entreprises, avec un objectif de stabilité, de sécurité et d'efficacité opérationnelle.",
    },
    {
      icon: Cloud,
      title: "Abonnements et licences",
      text: "CARTHY intègre la gestion des licences à une vision globale de votre environnement afin d'assurer la cohérence entre les outils, les besoins opérationnels et les exigences de sécurité.",
    },
    {
      icon: Shield,
      title: "Conformité SOC 2 et ISO 27001",
      text: "Nous aidons les organisations à renforcer leur gouvernance de l'information, réduire les écarts de sécurité et mieux répondre aux attentes des clients, partenaires et auditeurs.",
    },
    {
      icon: Cpu,
      title: "CARTHY AI",
      text: "Une intelligence artificielle privée exécutée localement afin que les données, les traitements et les connaissances de l'organisation demeurent sous contrôle interne.",
    },
  ];

  return (
    <section id="services" className="relative py-28 md:py-36 border-y border-white/10">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          overline="Nos services"
          title="Des services pensés pour la sécurité, la conformité et la performance"
          text="Une offre structurée pour les entreprises qui ont besoin d'un partenaire capable d'intervenir à la fois sur la technologie, la gouvernance et l'évolution de leur environnement numérique."
        />

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          transition={{ staggerChildren: 0.08 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card
                key={service.title}
                testid={`service-${index}`}
                image={index === 0 ? IMG.dataCenter : index === 3 ? IMG.wires : undefined}
                className="min-h-[300px]"
              >
                <Icon className="text-[#76b900]" size={30} />
                <h3 className="font-display font-semibold text-2xl mt-6">{service.title}</h3>
                <p className="mt-3 text-white/60 font-light max-w-xl">{service.text}</p>
              </Card>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

const CarthyAI = () => {
  const benefits = [
    "Les données sensibles et les connaissances internes restent dans l'environnement de l'organisation.",
    "Un agent local permet aux employés d'interagir naturellement avec l'IA de l'entreprise.",
    "Les accès peuvent être cloisonnés par département, équipe, fonction ou catégorie d'utilisateur.",
    "Les modèles peuvent être adaptés aux processus, à la terminologie et aux réalités métier.",
  ];

  return (
    <section id="carthy-ai" className="relative py-28 md:py-36 overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
          <Overline>CARTHY AI</Overline>
          <h2 className="font-display font-bold tracking-tighter text-4xl md:text-6xl mt-4">
            Une IA privée, locale et <span className="text-[#76b900]">souveraine</span>
          </h2>
          <p className="mt-6 text-white/65 text-lg font-light">
            CARTHY AI a été conçue pour les organisations qui souhaitent bénéficier de la puissance de
            l'intelligence artificielle sans dépendre d'une infrastructure infonuagique externe. Les modèles
            sont exécutés localement sur les serveurs ou l'environnement virtualisé de l'entreprise.
          </p>
          <p className="mt-5 text-white/65 text-lg font-light">
            Les données ne quittent jamais le périmètre de sécurité de l'organisation.
          </p>
          <a href="#contact" className="mt-9 inline-flex items-center gap-2 text-[#76b900] font-semibold hover:gap-3 transition-all">
            Découvrir CARTHY AI <ArrowRight size={18} />
          </a>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          transition={{ staggerChildren: 0.08 }}
          className="space-y-4"
        >
          {benefits.map((benefit) => (
            <motion.div key={benefit} variants={fadeUp} className="liquid-glass rounded-2xl p-6 flex gap-4 items-start">
              <Check size={20} className="text-[#76b900] shrink-0 mt-0.5" />
              <p className="text-white/75 font-light">{benefit}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Compliance = () => {
  const points = [
    "Préparer l'organisation à répondre à des exigences de sécurité plus élevées.",
    "Réduire les risques liés à la gouvernance des données et aux accès non autorisés.",
    "Soutenir la croissance commerciale grâce à une meilleure crédibilité auprès des clients et partenaires.",
  ];

  return (
    <section id="compliance" className="relative py-28 md:py-36 border-y border-white/10 bg-black">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          overline="Conformité"
          title="Faites de la conformité un avantage opérationnel"
          text="À mesure que les audits deviennent plus rigoureux et que les attentes des clients augmentent, la conformité ne peut plus être traitée comme une simple formalité. CARTHY vous aide à structurer votre sécurité, démontrer votre maturité et bâtir une relation de confiance durable."
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-16 items-stretch">
          <Card className="lg:col-span-2 min-h-[320px]" spotlight>
            <Globe className="text-[#76b900]" size={30} />
            <div className="font-display font-black text-4xl mt-8">SOC 2</div>
            <p className="mt-3 text-white/60">Type 1 et Type 2</p>
            <div className="font-display font-black text-4xl mt-8">ISO 27001</div>
            <p className="mt-3 text-white/60">Gouvernance et sécurité de l'information</p>
          </Card>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            transition={{ staggerChildren: 0.08 }}
            className="lg:col-span-3 grid gap-4"
          >
            {points.map((point) => (
              <motion.div key={point} variants={fadeUp} className="liquid-glass rounded-2xl p-6 flex items-start gap-4">
                <Shield size={22} className="text-[#76b900] shrink-0" />
                <p className="text-white/75 font-light">{point}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Sectors = () => {
  const sectors = [
    { icon: Activity, name: "Finance", text: "Protection des données, traçabilité et exigences élevées de gouvernance." },
    { icon: Shield, name: "Santé", text: "Confidentialité des informations et contrôle strict des accès." },
    { icon: Network, name: "Technologies", text: "Sécurité, conformité et crédibilité auprès des clients et partenaires." },
    { icon: Zap, name: "Industrie et opérations critiques", text: "Résilience des systèmes et continuité des activités sensibles." },
  ];

  return (
    <section id="sectors" className="relative py-28 md:py-36">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          overline="Secteurs desservis"
          title="Des solutions adaptées aux secteurs les plus exigeants"
          text="CARTHY cible en priorité les organisations pour lesquelles la sécurité, la traçabilité et la conformité ont un impact direct sur les opérations et la réputation."
          centered
        />

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          transition={{ staggerChildren: 0.08 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16"
        >
          {sectors.map((sector) => {
            const Icon = sector.icon;
            return (
              <Card key={sector.name}>
                <Icon className="text-[#76b900]" size={27} />
                <h3 className="font-display font-semibold text-xl mt-6">{sector.name}</h3>
                <p className="mt-3 text-sm text-white/60 font-light">{sector.text}</p>
              </Card>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

const Method = () => {
  const steps = [
    "Analyse des besoins et du niveau de maturité.",
    "Recommandations alignées sur les objectifs de sécurité et de conformité.",
    "Mise en place des solutions et structuration des accès.",
    "Optimisation continue selon les résultats et les besoins d'évolution.",
  ];

  return (
    <section id="method" className="relative py-28 md:py-36 border-y border-white/10 bg-black">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeader
          overline="Notre méthode"
          title="Une mise en œuvre structurée et progressive"
          text="Une approche mesurable, adaptée à la réalité de l'organisation et conçue pour faire évoluer la sécurité, la conformité et l'infrastructure sans complexité inutile."
        />

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          transition={{ staggerChildren: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16"
        >
          {steps.map((step, index) => (
            <motion.div key={step} variants={fadeUp} className="liquid-glass rounded-2xl p-7">
              <span className="font-mono text-xs tracking-[0.25em] text-[#76b900]">0{index + 1}</span>
              <p className="mt-6 text-white/75 font-light">{step}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const About = () => (
  <section id="about" className="relative py-28 md:py-36 overflow-hidden">
    <div className="absolute inset-0 grid-lines opacity-25" />
    <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <SectionHeader
        overline="À propos"
        title="À propos de CARTHY"
        text="CARTHY se positionne comme un acteur spécialisé en infrastructure, en gestion d'infrastructure informatique et en conformité organisationnelle. Notre offre est axée sur la fiabilité, la maîtrise des données et l'accompagnement des organisations dans des contextes où les exigences de sécurité sont élevées."
      />
      <Card spotlight>
        <Lock className="text-[#76b900]" size={30} />
        <h3 className="font-display font-semibold text-3xl mt-7">Notre différence</h3>
        <p className="mt-4 text-white/65 text-lg font-light">
          L'intégration d'une IA privée locale et une expertise centrée sur SOC 2 et ISO 27001 afin de
          réunir infrastructure, sécurité, gouvernance et souveraineté des données dans une même approche.
        </p>
      </Card>
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" className="relative py-28 md:py-36 border-t border-white/10 bg-black">
    <div className="mx-auto max-w-5xl px-6 text-center">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
        <Overline>Échangeons</Overline>
        <h2 className="font-display font-bold tracking-tighter text-4xl md:text-6xl mt-4">
          Parlons de votre infrastructure, de votre conformité et de vos données
        </h2>
        <p className="mt-6 mx-auto max-w-3xl text-white/65 text-lg font-light">
          Que vous souhaitiez renforcer votre posture de sécurité, avancer vers une conformité reconnue
          ou déployer une IA privée dans votre environnement, CARTHY peut vous accompagner avec une
          approche structurée, sécurisée et adaptée à votre réalité d'affaires.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <a
            href="mailto:contact@carthy.io?subject=Demande%20de%20consultation%20CARTHY"
            className="inline-flex items-center gap-2 rounded-full bg-[#76b900] px-7 py-3.5 text-sm font-semibold text-black hover:bg-[#8ce500] transition-colors shadow-[0_0_32px_rgba(118,185,0,0.35)]"
          >
            Demander une consultation <ArrowRight size={16} />
          </a>
          <a
            href="#services"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:border-[#76b900] hover:text-[#76b900] transition-colors"
          >
            Évaluer notre environnement
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

export const Footer = ({ homePath = "" }) => {
  const columns = [
    { title: "Solutions", links: [["Produits", "/produits"], ["Infrastructure", "#services"], ["CARTHY AI", "#carthy-ai"], ["Conformité", "#compliance"]] },
    { title: "Entreprise", links: [["Pourquoi CARTHY", "#why"], ["À propos", "#about"], ["Contact", "#contact"]] },
    { title: "Référentiels", links: [["SOC 2 Type 1", "#compliance"], ["SOC 2 Type 2", "#compliance"], ["ISO 27001", "#compliance"]] },
  ];

  return (
    <footer className="relative bg-black border-t border-white/10 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 pt-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <Logo size={40} testid="footer-logo" />
            <p className="mt-4 text-white/50 text-sm font-light max-w-xs">
              Infrastructure, conformité et intelligence artificielle privée pour les organisations qui veulent garder le contrôle de leurs données.
            </p>
          </div>
          {columns.map((column) => (
            <div key={column.title}>
              <h4 className="font-mono text-xs tracking-[0.2em] uppercase text-white/40">{column.title}</h4>
              <ul className="mt-5 space-y-3">
                {column.links.map(([label, href]) => (
                  <li key={label}>
                    <a href={href.startsWith("#") ? `${homePath}${href}` : href} className="text-sm text-white/70 hover:text-[#76b900] transition-colors">{label}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-4 py-8 border-t border-white/10 font-mono text-xs text-white/40">
          <span>© 2026 CARTHY. Tous droits réservés.</span>
          <div className="flex flex-wrap justify-center gap-6">
            <a href="#" className="hover:text-white transition-colors">Confidentialité</a>
            <a href="#" className="hover:text-white transition-colors">Conditions</a>
            <a href="mailto:contact@carthy.io" className="hover:text-white transition-colors">contact@carthy.io</a>
          </div>
        </div>
      </div>

      <div aria-hidden className="select-none pointer-events-none leading-none">
        <h2 className="font-display font-black tracking-tighter text-center text-[24vw] text-white/[0.03]">CARTHY</h2>
      </div>
    </footer>
  );
};

const LandingPage = () => (
  <div className="relative bg-black text-white min-h-screen antialiased overflow-x-hidden">
    <NetworkBackground />
    <div className="relative z-10">
      <Navbar />
      <Hero />
      <TrustStrip />
      <WhyCarthy />
      <Services />
      <Compliance />
      <CarthyAI />
      <Sectors />
      <Method />
      <About />
      <Contact />
      <Footer />
    </div>
  </div>
);

export default LandingPage;
